import {
	DeleteOutlined,
	PlusOutlined,
	CloseOutlined,
	SyncOutlined,
} from "@ant-design/icons";
import { Divider, Image, Upload } from "antd";
import ImageWrapper from "components/image/ImageWrapper";
import { CAR_MEDIA_RULE } from "constants/constants";
import React, {
	Dispatch,
	ReactNode,
	SetStateAction,
	useRef,
	useState,
} from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { BaseComponentProps, CarMediaError } from "types";
import { customRequestUpload, getLastItemInArray, translate } from "utils";

export interface PhotoItemProps {
	path: string;
	url: string;
	classifiedType: string;
	position: number;
	color?: string;
	type?: string;
}

interface BasePhotoProps {
	onDelete?: (photo: PhotoItemProps) => void;
	viewOnly?: boolean;
}

const MAXIMUM_DESC = {
	internal: "Maximum 14 photos",
	external: "Maximum 16 photos",
};

export interface MovableItemProps extends BasePhotoProps {
	photo: PhotoItemProps;
	index: number;
	order: number;
	position: number;
	changePhotoColumnRef: React.MutableRefObject<number>;
	swapItem: {
		position: number;
		classifiedType: string;
	};
	handleSwapPosition: (position: number, classifiedType: string) => void;
	handleDragItem: (dragIndex: number, hoverIndex: number) => void;
	handleDragItemDiffColumn: (
		currentItem: PhotoItemProps,
		photoType: string,
		position: number,
	) => void;
	photos?: PhotoItemProps[];
}

interface Props extends BaseComponentProps, BasePhotoProps {
	photos: PhotoItemProps[];
	setPhotos: (oldPhotos: PhotoItemProps[]) => void;
	// onAdd: (props: onAddProps) => void;
	onAdd: any;
	maximumPhotos: number;
	error: CarMediaError;
}

const MovableItem = ({
	photo,
	index,
	order,
	position,
	viewOnly,
	onDelete,
	changePhotoColumnRef,
	swapItem,
	handleSwapPosition,
	handleDragItem,
	handleDragItemDiffColumn,
	photos,
}: MovableItemProps) => {
	const ref = useRef<HTMLInputElement>(null);

	const [, drop] = useDrop({
		accept: ["internal", "external"],
		drop: (item: {
			position: number;
			index: number;
			classifiedType: string;
		}) => {
			if (!ref.current) {
				return;
			}

			const dragIndex = item.position; // item is dragging
			const hoverIndex = position; // item is hovered

			if (dragIndex === hoverIndex) {
				return;
			}

			const classifiedType = photo.classifiedType;

			if (item.classifiedType !== classifiedType) {
				changePhotoColumnRef.current = hoverIndex;
				return;
			}

			handleDragItem(dragIndex, hoverIndex);
		},
	});

	const [, drag] = useDrag({
		type: photo.classifiedType,
		item: {
			path: photo.path,
			url: photo.url,
			position,
			classifiedType: photo.classifiedType,
		},
		end: (item, monitor) => {
			const dropResult = monitor.getDropResult() as { name: string };

			if (dropResult && dropResult.name !== item.classifiedType) {
				const { name } = dropResult as { name: string };

				if (name)
					handleDragItemDiffColumn(item, name, changePhotoColumnRef.current);
			}
		},
	});

	drag(drop(ref));

	return (
		<div ref={ref} key={index} className="photo-delete">
			{!viewOnly && (
				<span
					className="flex"
					onClick={() => {
						onDelete && onDelete(photo);
					}}
				>
					<DeleteOutlined />
				</span>
			)}
			{((!viewOnly && !swapItem.classifiedType) ||
				swapItem.classifiedType === photo.classifiedType) && (
				<span
					className={`${
						swapItem.position === position && "!bg-red-500"
					}  flex left-0`}
					onClick={() => {
						handleSwapPosition(position, photo.classifiedType);
					}}
				>
					{swapItem.position === position ? (
						<CloseOutlined />
					) : (
						<SyncOutlined />
					)}
				</span>
			)}

			<div>
				<ImageWrapper
					key={index}
					width={150}
					className="slider-image z-0"
					src={photo.url}
					order={order}
					photos={photos}
				/>
			</div>
		</div>
	);
};

const Column = ({
	children,
	title,
	errorMessage,
}: {
	children: ReactNode;
	title: string;
	errorMessage?: string;
}) => {
	const [{ isOver, canDrop }, drop] = useDrop({
		accept: ["internal", "external"],
		drop: () => ({ name: title }),
		collect: monitor => ({
			isOver: monitor.isOver(),
			canDrop: monitor.canDrop(),
		}),
	});

	const getBackgroundColor = () => {
		if (isOver) {
			return canDrop ? "rgb(230,251,255)" : "rgb(255,188,188)";
		}
		return "";
	};

	return (
		<div>
			<div
				className={`border border-solid ${
					errorMessage ? "border-[#ff4d4f]" : "border-slate-500"
				}`}
				ref={drop}
				style={{ backgroundColor: getBackgroundColor() }}
			>
				{children}
			</div>
			{errorMessage && (
				<p className="text-[#ff4d4f]">{translate(errorMessage)}</p>
			)}
		</div>
	);
};

const MultipleDragImage: React.FC<Props> = ({
	photos,
	setPhotos,
	onAdd,
	onDelete,
	maximumPhotos,
	viewOnly = false,
	error,
}) => {
	const changePhotoColumnRef = useRef<number>(0);
	const defaultSwapItem = {
		position: 0,
		classifiedType: "",
	};
	const [swapItem, setSwapItem] = useState(defaultSwapItem);

	const handleDragItem = (dragPosition: number, hoverPosition: number) => {
		const dragIndex = photos.findIndex(item => item.position === dragPosition);
		if (dragIndex !== -1) {
			const tempArray = [...photos];
			const hoverIndex = tempArray.findIndex(
				item => item.position === hoverPosition,
			);
			const dragItem = tempArray.splice(dragIndex, 1);
			tempArray.splice(hoverIndex, 0, dragItem[0]);
			setPhotos(tempArray);
		}
	};

	const handleDragItemDiffColumn = (
		currentItem: PhotoItemProps,
		photoType: string,
		position: number,
	) => {
		currentItem.classifiedType = photoType;
		// check if have at least 1 photo in the column photo prepare to move
		const haveAtLeast1Photo = photos.find(
			item => item.classifiedType === photoType,
		);
		const tempArray = [...photos];
		const itemCurrentIndex = tempArray.findIndex(
			item => item.position === currentItem.position,
		);
		const itemReplaceIndex = tempArray.findIndex(
			item => item.position === position,
		);
		// splice the current photo
		tempArray.splice(itemCurrentIndex, 1);

		//add current photo to the end of list photos
		if (!haveAtLeast1Photo) tempArray.splice(tempArray.length, 0, currentItem);
		else if (itemReplaceIndex === -1) {
			// drag image but not override another image
			const condition = (item: PhotoItemProps) =>
				item.classifiedType === photoType;
			tempArray.splice(
				getLastItemInArray(tempArray, condition) + 1,
				0,
				currentItem,
			);
		} else {
			if (itemCurrentIndex < itemReplaceIndex)
				tempArray.splice(itemReplaceIndex - 1, 0, currentItem);
			else tempArray.splice(itemReplaceIndex, 0, currentItem);
		}

		changePhotoColumnRef.current = 0;
		setPhotos(tempArray);
	};

	const handleSwapPosition = (position: number, classifiedType: string) => {
		if (swapItem.position) {
			if (swapItem.position === position) setSwapItem(defaultSwapItem);
			else {
				const dragIndex = photos.findIndex(item => item.position === position);
				if (dragIndex !== -1) {
					const tempArray = [...photos];
					const hoverIndex = tempArray.findIndex(
						item => item.position === swapItem.position,
					);
					const hoverItem = tempArray.splice(
						hoverIndex,
						1,
						tempArray[dragIndex],
					);
					tempArray.splice(dragIndex, 1, hoverItem[0]);
					setSwapItem(defaultSwapItem);
					setPhotos(tempArray);
				}
			}
		} else {
			setSwapItem({ position, classifiedType });
		}
	};

	const returnItemsForColumn = (
		photoType: "internal" | "external",
		maxItems: number,
		maximumDesc: string,
	) => {
		const pts = photos.filter(item => item.classifiedType === photoType);
		return (
			<div className="flex flex-wrap gap-5 pb-5 items-center justify-center">
				<Image.PreviewGroup>
					{pts.map(
						(photo, index) =>
							photo && (
								<MovableItem
									key={index}
									index={index}
									photo={photo}
									position={photo.position}
									onDelete={onDelete}
									handleSwapPosition={handleSwapPosition}
									handleDragItem={handleDragItem}
									handleDragItemDiffColumn={handleDragItemDiffColumn}
									changePhotoColumnRef={changePhotoColumnRef}
									swapItem={swapItem}
									photos={photos}
									order={photos.findIndex(p => p === photo)}
								/>
							),
					)}
				</Image.PreviewGroup>

				{!viewOnly && (
					<Upload
						listType="picture-card"
						className="w-auto"
						accept="image/*, .heic, .heif"
						onChange={({ ...props }) => onAdd({ ...props, photoType })}
						disabled={pts.length >= maxItems}
						multiple
						fileList={[]}
						customRequest={customRequestUpload}
					>
						<div>
							<PlusOutlined />
							<p>{translate("UPLOAD")}</p>
							<p className="text-xs italic">{`(${translate(maximumDesc)})`}</p>
						</div>
					</Upload>
				)}
			</div>
		);
	};

	return (
		<div className="grid grid-cols-2 gap-8 mx-[6.75rem]">
			<DndProvider backend={HTML5Backend}>
				<Column title={"external"} errorMessage={error.external}>
					<p className="pl-5 pt-3 text-base font-medium flex justify-start">
						{translate("External photos")}
					</p>

					{returnItemsForColumn(
						"external",
						CAR_MEDIA_RULE.external.maxLength.value,
						MAXIMUM_DESC.external,
					)}
				</Column>
				<Column title={"internal"} errorMessage={error.internal}>
					<p className="pl-5 pt-3 text-base font-medium flex justify-start">
						{translate("Internal photos")}
					</p>

					{returnItemsForColumn(
						"internal",
						CAR_MEDIA_RULE.internal.maxLength.value,
						MAXIMUM_DESC.internal,
					)}
				</Column>
			</DndProvider>
		</div>
	);
};

export default MultipleDragImage;
